/**
 * SalesReportTab - Comprehensive sales reporting
 * Shows sales breakdown by payment method, items sold, and time periods
 * All figures shown as NET revenue (excluding 12.5% VAT)
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, DollarSign, CreditCard, Smartphone, Banknote, RefreshCw, 
  Download, Package, TrendingUp, ChevronDown, QrCode, Globe, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function SalesReportTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    const fijiOffset = 12 * 60;
    const localOffset = now.getTimezoneOffset();
    const fijiTime = new Date(now.getTime() + (fijiOffset + localOffset) * 60000);
    return fijiTime.toISOString().split('T')[0];
  });
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [expandedItem, setExpandedItem] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics/sales-report?period=${period}&date=${selectedDate}`);
      setData(res.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      toast.error('Failed to load sales report');
    }
    setLoading(false);
  }, [period, selectedDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const getPaymentIcon = (method) => {
    const icons = {
      cash: <Banknote className="w-5 h-5 text-green-600" />,
      card: <CreditCard className="w-5 h-5 text-blue-600" />,
      eftpos: <CreditCard className="w-5 h-5 text-blue-600" />,
      mpaisa: <Smartphone className="w-5 h-5 text-red-600" />,
      mpaisa_qr: <QrCode className="w-5 h-5 text-red-600" />,
      online: <Globe className="w-5 h-5 text-purple-600" />,
      visa: <CreditCard className="w-5 h-5 text-indigo-600" />,
      other: <DollarSign className="w-5 h-5 text-gray-600" />
    };
    return icons[method] || icons.other;
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-report-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-foreground">Sales Report</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Comprehensive breakdown by payment method and items sold (NET revenue)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchReport}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Period Selector */}
      <div className="bg-card rounded-xl p-4 border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daily</SelectItem>
              <SelectItem value="week">Weekly</SelectItem>
              <SelectItem value="month">Monthly</SelectItem>
              <SelectItem value="quarter">Quarterly</SelectItem>
              <SelectItem value="halfyear">6 Months</SelectItem>
              <SelectItem value="year">Yearly</SelectItem>
            </SelectContent>
          </Select>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-background rounded-lg border border-border">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none outline-none text-foreground text-sm"
            />
          </div>
        </div>
        
        {data && (
          <div className="text-sm text-muted-foreground font-medium">
            {data.period_label}
          </div>
        )}
      </div>

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 opacity-80" />
                <span className="text-green-100 text-sm">Net Revenue</span>
              </div>
              <p className="text-3xl font-bold">${data.summary.net_revenue.toFixed(2)}</p>
              <p className="text-xs text-green-100 mt-1">What the cafe keeps</p>
            </div>
            
            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-muted-foreground text-sm">Collected</span>
              </div>
              <p className="text-2xl font-bold text-foreground">${data.summary.gross_revenue.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total received</p>
            </div>
            
            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-5 h-5 text-purple-600" />
                <span className="text-muted-foreground text-sm">VAT (12.5%)</span>
              </div>
              <p className="text-2xl font-bold text-foreground">${data.summary.vat_collected.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Owed to FRCS</p>
            </div>
            
            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-5 h-5 text-amber-600" />
                <span className="text-muted-foreground text-sm">Transactions</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{data.summary.total_transactions}</p>
              <p className="text-xs text-muted-foreground mt-1">Total sales</p>
            </div>
          </div>

          {/* Payment Method Breakdown */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h2 className="font-heading text-lg text-foreground mb-4">Payment Method Breakdown</h2>
            
            {Object.keys(data.payment_breakdown).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No sales for this period</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(data.payment_breakdown).map(([method, info]) => (
                  <div key={method} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedPayment(expandedPayment === method ? null : method)}
                      className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {getPaymentIcon(method)}
                        <div className="text-left">
                          <p className="font-medium text-foreground">{info.label}</p>
                          <p className="text-sm text-muted-foreground">{info.count} transaction(s)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-foreground">${info.net_revenue.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">net</p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${
                          expandedPayment === method ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </button>
                    
                    {expandedPayment === method && info.transactions.length > 0 && (
                      <div className="border-t border-border bg-muted/30 p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-muted-foreground text-left">
                                <th className="pb-2 font-medium">Order</th>
                                <th className="pb-2 font-medium">Date/Time</th>
                                <th className="pb-2 font-medium">Gross</th>
                                <th className="pb-2 font-medium">Net</th>
                                {method === 'mpaisa_qr' && <th className="pb-2 font-medium">Ref Code</th>}
                                <th className="pb-2 font-medium">Customer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {info.transactions.map((txn, idx) => (
                                <tr key={idx} className="border-t border-border/50">
                                  <td className="py-2 font-medium">{txn.order_number}</td>
                                  <td className="py-2 text-muted-foreground">{txn.date} {txn.time}</td>
                                  <td className="py-2">${txn.total?.toFixed(2)}</td>
                                  <td className="py-2 font-medium text-green-600">${txn.net?.toFixed(2)}</td>
                                  {method === 'mpaisa_qr' && (
                                    <td className="py-2">
                                      <span className="font-mono bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-0.5 rounded text-xs">
                                        {txn.mpaisa_ref_code || 'N/A'}
                                      </span>
                                    </td>
                                  )}
                                  <td className="py-2 text-muted-foreground">{txn.customer_name || 'Walk-in'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Items Sold Breakdown */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h2 className="font-heading text-lg text-foreground mb-4">Items Sold</h2>
            
            {data.items_sold.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No items sold this period</p>
            ) : (
              <div className="space-y-3">
                {data.items_sold.map((item, idx) => (
                  <div key={idx} className="border border-border rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedItem(expandedItem === item.name ? null : item.name)}
                      className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                          <Package className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-foreground">{item.name}</p>
                          <p className="text-sm text-muted-foreground">{item.quantity} sold</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-foreground">${item.net_revenue.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">net revenue</p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${
                          expandedItem === item.name ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </button>
                    
                    {expandedItem === item.name && Object.keys(item.by_payment_method).length > 0 && (
                      <div className="border-t border-border bg-muted/30 p-4">
                        <p className="text-sm font-medium text-muted-foreground mb-3">By Payment Method:</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                          {Object.entries(item.by_payment_method).map(([pm, pmData]) => (
                            <div key={pm} className="flex items-center gap-2 p-2 bg-background rounded-lg border border-border">
                              {getPaymentIcon(pm)}
                              <div>
                                <p className="text-sm font-medium text-foreground">{pmData.quantity} sold</p>
                                <p className="text-xs text-muted-foreground">${pmData.gross.toFixed(2)} gross</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
